import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User2, Briefcase, Mail, Clock, Check, Ban, Clock3, RotateCcw, MessageSquare, Flag, NotebookPen, Send } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

import { StatusBadge, ActionButton, SectionHeader, CurrentStateLine } from './VisualAtoms';
import { toneForTicketStatus, TICKET_DECISION_VARIANTS } from '../theme/adminVisualSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TicketDrawer — right-side curator panel for a ticket request.
 */
const DECISION_ACTIONS = [
  { value: 'accepted', label: 'Accept', icon: Check },
  { value: 'declined', label: 'Decline', icon: Ban },
  { value: 'waitlist', label: 'Waitlist', icon: Clock3 },
  { value: 'pending', label: 'Restore to Pending', icon: RotateCcw },
];

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function Row({ icon: Icon, label, value, mono = true, children }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-3 h-3 text-[#90E0EF]/80" />}
        <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">{label}</p>
      </div>
      {children ? children : (
        <p className={`text-sm ${mono ? 'font-mono' : 'font-outfit'} text-white break-words`}>
          {value || <span className="text-[#444]">—</span>}
        </p>
      )}
    </div>
  );
}

export default function TicketDrawer({ ticket, onClose, onUpdate }) {
  const [current, setCurrent] = useState(ticket);
  const [note, setNote] = useState(ticket?.internal_note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurrent(ticket);
    setNote(ticket?.internal_note || '');
  }, [ticket]);

  const open = !!ticket;

  const patch = async (body) => {
    if (!current) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/tickets/${current.id}`, body);
      setCurrent(res.data);
      onUpdate && onUpdate(res.data);
      toast.success('Updated');
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const currentDecision = (current?.decision_status || 'pending');

  return (
    <AnimatePresence>
      {open && current && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/55 backdrop-blur-[2px] z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            data-testid="ticket-drawer-overlay"
          />

          {/* Drawer */}
          <motion.aside
            key={current.id}
            className="fixed right-0 top-0 bottom-0 z-[101] w-full sm:w-[500px] bg-[#070707] border-l border-[#1a1a1a] overflow-y-auto flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="ticket-drawer"
          >
            {/* Header */}
            <header className="sticky top-0 z-10 bg-[#070707]/95 backdrop-blur-sm border-b border-[#1a1a1a] px-5 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">
                  Ticket Request
                </p>
                <h2 className="font-outfit text-xl font-semibold text-white truncate mt-0.5">
                  {current.name || 'Unnamed'}
                </h2>
                <p className="text-xs font-mono text-[#999] mt-0.5 truncate">{current.email}</p>
                <div className="mt-2 flex items-center gap-1.5">
                  <StatusBadge status={currentDecision} kind="ticket" strong />
                  {current.source === 'invite' && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] font-mono px-2 py-0.5 border uppercase tracking-[0.18em]"
                      style={{ color: '#c89dff', borderColor: 'rgba(200,157,255,0.45)', background: 'rgba(200,157,255,0.08)' }}
                      data-testid="ticket-invite-badge"
                    >
                      <Send className="w-2.5 h-2.5" /> Invite
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                data-clickable="true"
                data-testid="ticket-drawer-close"
                aria-label="Close"
                className="w-8 h-8 border border-[#222] text-[#888] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="p-5 space-y-6">
              {/* Identity */}
              <section data-testid="drawer-identity-block">
                <SectionHeader
                  icon={User2}
                  title="Identity"
                  helper="Who is requesting access to the exhibition."
                />
                <div className="grid grid-cols-2 gap-px mt-2">
                  <Row icon={User2} label="Name" value={current.name} mono={false} />
                  <Row icon={Briefcase} label="Profession" value={current.profession} />
                  <div className="col-span-2">
                    <Row icon={Mail} label="Email">
                      <a
                        href={`mailto:${current.email}`}
                        data-clickable="true"
                        className="text-sm font-mono text-[#90E0EF] hover:text-white transition-colors break-all"
                      >
                        {current.email}
                      </a>
                    </Row>
                  </div>
                </div>
              </section>

              {/* Status timeline */}
              <section data-testid="drawer-status-block">
                <SectionHeader
                  icon={Clock}
                  title="Status · Timeline"
                  helper="When this request moved through each stage."
                />
                <div className="grid grid-cols-2 gap-px mt-2">
                  <Row icon={Clock} label="Requested" value={fmt(current.created_at)} />
                  <Row icon={Clock} label="Reviewed" value={fmt(current.reviewed_at)} />
                  <Row icon={Check} label="Accepted" value={fmt(current.accepted_at)} />
                  <Row icon={Ban} label="Declined" value={fmt(current.declined_at)} />
                </div>
              </section>

              {/* Actions */}
              <section data-testid="drawer-actions-block">
                <SectionHeader
                  icon={Flag}
                  title="Decision Actions"
                  helper="Choose the outcome for this ticket request."
                />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {DECISION_ACTIONS.map((a) => {
                    const isCurrent = currentDecision === a.value;
                    return (
                      <ActionButton
                        key={a.value}
                        variant={TICKET_DECISION_VARIANTS[a.value]}
                        active={isCurrent}
                        disabled={saving || isCurrent}
                        onClick={() => patch({ decision_status: a.value })}
                        icon={a.icon}
                        data-testid={`drawer-action-${a.value}`}
                      >
                        {a.label}
                      </ActionButton>
                    );
                  })}
                </div>
                <CurrentStateLine
                  label="Current decision"
                  value={currentDecision}
                  tone={toneForTicketStatus(currentDecision)}
                />
              </section>

              {/* Internal note */}
              <section data-testid="drawer-note-block">
                <SectionHeader
                  icon={NotebookPen}
                  title="Internal Note"
                  helper="Private internal notes visible only to curators."
                />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  placeholder="Only visible to curators…"
                  data-testid="drawer-note-input"
                  className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2.5 resize-none focus:outline-none focus:border-[#90E0EF] placeholder:text-[#444] mt-1"
                />
                <div className="mt-2">
                  <ActionButton
                    variant="primary"
                    disabled={saving}
                    onClick={() => patch({ internal_note: note })}
                    data-testid="drawer-save-note"
                    icon={NotebookPen}
                  >
                    Save Note
                  </ActionButton>
                </div>
              </section>

              {/* Communication */}
              <section data-testid="drawer-communication-block">
                <SectionHeader
                  icon={MessageSquare}
                  title="Communication"
                  helper="Most recent outbound contact with this applicant."
                />
                <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3 mt-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-3 h-3 text-[#90E0EF]/80" />
                    <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">
                      Status
                    </p>
                  </div>
                  <p className="text-sm font-mono text-white mt-1.5">
                    {current.communication_status || <span className="text-[#444]">— no contact yet</span>}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] font-mono">
                    <div>
                      <p className="text-[#777] uppercase tracking-[0.15em]">Last email type</p>
                      <p className="text-white mt-1">
                        {current.last_email_type || <span className="text-[#444]">—</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#777] uppercase tracking-[0.15em]">Last email sent</p>
                      <p className="text-white mt-1">{fmt(current.last_email_sent_at)}</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Invite source */}
              {current.source === 'invite' && (
                <section data-testid="drawer-invite-block">
                  <SectionHeader
                    icon={Send}
                    title="Invite Source"
                    helper="This ticket request came from an Invite OS tracking link."
                  />
                  <div className="border p-3 mt-2" style={{ borderColor: 'rgba(200,157,255,0.25)', background: 'rgba(200,157,255,0.04)' }}>
                    <p className="text-[10px] font-mono text-[#999] tracking-[0.15em] uppercase">Tracked invite</p>
                    <p className="text-sm font-mono text-white mt-1">{current.source_label || 'invite'}</p>
                    {current.invite_thread_id && (
                      <div className="mt-3">
                        <RouterLink
                          to="/admin/invite"
                          data-clickable="true"
                          data-testid="drawer-open-invite-thread"
                          className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2.5 py-1.5 border uppercase tracking-[0.14em]"
                          style={{ color: '#c89dff', borderColor: 'rgba(200,157,255,0.45)' }}
                        >
                          <Send className="w-3 h-3" /> Open Invite Module
                        </RouterLink>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>

            {/* Footer */}
            <footer className="mt-auto border-t border-[#1a1a1a] px-5 py-3 text-[10px] font-mono text-[#555] flex items-center justify-between">
              <span>ESC to close</span>
              <span className="truncate ml-4">ID · {current.id}</span>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
